import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { thinkspacesService } from '@/services/collections/thinkspaces';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateThinkSpaceInput } from '@/types/thinkspace';

export const useMyThinkSpaces = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['thinkspaces', 'owned', user?.uid],
    queryFn: () => thinkspacesService.getOwned(user!.uid),
    enabled: !!user,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useSharedThinkSpaces = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['thinkspaces', 'shared', user?.email],
    queryFn: () => thinkspacesService.getSharedWithMe(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const usePendingInvitations = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['thinkspaces', 'pending', user?.email],
    queryFn: () => thinkspacesService.getPendingInvitations(user!.email!),
    enabled: !!user?.email,
    refetchOnMount: 'always',
    staleTime: 0,
  });
};

export const useCreateThinkSpace = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateThinkSpaceInput) =>
      thinkspacesService.create(input, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};

export const useDeleteThinkSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => thinkspacesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};

export const useRespondToInvitation = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) =>
      thinkspacesService.respondToInvitation(id, user!.email!, accept, user!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thinkspaces'] });
    },
  });
};
